/**
 * OpenSpec 006 · D8 — los avisos del alta cuando la ficha nació con datos del ecosistema.
 *
 * Cuando la persona ya existía, el alta reusa su identidad y **ganan los datos que ya tenía**: el
 * mail o el WhatsApp que el operador tipeó puede no ser el que queda en la ficha. Eso no se puede
 * dejar en silencio — si no, el operador descubre después un contacto con datos que él no cargó y
 * no tiene forma de saber por qué.
 *
 * El backend devuelve dos listas en la respuesta de `POST /contacts`, que significan cosas
 * distintas y por eso se muestran distinto:
 *
 * - `hydratedFields`: la identidad ya tenía ese dato y ganó el suyo. La ficha quedó **con el dato
 *   del ecosistema** → mensaje de éxito.
 * - `discardedFields`: lo tipeado era de otra persona y se descartó **sin reemplazo**. La ficha
 *   quedó sin ese dato → advertencia.
 */

export type IdentityField = "email" | "whatsapp";

export type IdentityHydrationResult = {
  hydratedFields?: string[] | null;
  discardedFields?: string[] | null;
};

const FIELD_LABEL: Record<IdentityField, string> = {
  email: "Mail",
  whatsapp: "WhatsApp",
};

/** `["whatsapp"]` → `"WhatsApp"`; `["email","whatsapp"]` → `"Mail y WhatsApp"`. */
export const identityFieldsLabel = (fields?: string[] | null): string =>
  (fields ?? [])
    .filter((f): f is IdentityField => f === "email" || f === "whatsapp")
    .map((f) => FIELD_LABEL[f])
    .join(" y ");

type Translate = (key: string, options?: any) => string;

export const identityHydrationNotices = (
  result: IdentityHydrationResult | null | undefined,
  t: Translate,
): { hydrated: string | null; discarded: string | null } => {
  const hydrated = identityFieldsLabel(result?.hydratedFields);
  const discarded = identityFieldsLabel(result?.discardedFields);

  return {
    hydrated: hydrated
      ? t("contacts.message.identityHydrated", {
          fields: hydrated,
          defaultValue:
            "Actualizamos el {{fields}} del contacto con los datos existentes del Contacto en el Ecosistema XCONS",
        })
      : null,
    discarded: discarded
      ? t("contacts.message.identityDiscarded", {
          fields: discarded,
          defaultValue:
            "El {{fields}} que ingresaste pertenece a otro contacto del Ecosistema XCONS: no se guardó en esta ficha",
        })
      : null,
  };
};
